import { useRef } from 'react';
import { motion } from 'framer-motion';
import { Database, GitMerge, MessageSquare, FileText, CheckCircle2, Network, BarChart, Zap } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export const KnowledgeGraph = () => {
  const svgRef = useRef(null);
  const { t } = useLanguage();
  const lang = t('knowledgeGraph');

  const nodes = [
    // Output nodes (Top)
    { id: 'out1', x: 120, y: 100, label: lang.nodes.analyticsPanel, color: '#10b981', icon: BarChart },
    { id: 'output', x: 250, y: 80, label: lang.nodes.verifiedAnswer, color: '#10b981', icon: CheckCircle2 },
    { id: 'out2', x: 380, y: 100, label: lang.nodes.autonomousAgent, color: '#10b981', icon: Zap },

    // Middle tiers
    { id: 'verify', x: 250, y: 220, label: lang.nodes.factChecker, color: '#f59e0b', icon: Network },
    { id: 'doora', x: 250, y: 380, label: lang.nodes.dooraKnowledgeGraph, color: '#8b5cf6', icon: Database },

    // Embeddings & Indexing
    { id: 'embed1', x: 150, y: 540, label: lang.nodes.vectorIndex, color: '#6366f1', icon: Network },
    { id: 'embed2', x: 350, y: 540, label: lang.nodes.entityResolution, color: '#6366f1', icon: Network },

    // Input Source Nodes (Bottom)
    { id: 'src1', x: 80, y: 700, label: lang.nodes.githubPR, color: '#3b82f6', icon: GitMerge },
    { id: 'src2', x: 250, y: 720, label: lang.nodes.confluenceDocs, color: '#3b82f6', icon: FileText },
    { id: 'src3', x: 420, y: 700, label: lang.nodes.slackThread, color: '#3b82f6', icon: MessageSquare },
  ];

  const edges = [
    // Source -> pre-processing
    { from: 'src1', to: 'embed1', path: 'M 80 700 Q 115 620 150 540' },
    { from: 'src2', to: 'embed1', path: 'M 250 720 Q 200 630 150 540' },
    { from: 'src2', to: 'embed2', path: 'M 250 720 Q 300 630 350 540' },
    { from: 'src3', to: 'embed2', path: 'M 420 700 Q 385 620 350 540' },
    
    // pre-processing -> Doora
    { from: 'embed1', to: 'doora', path: 'M 150 540 Q 200 460 250 380' },
    { from: 'embed2', to: 'doora', path: 'M 350 540 Q 300 460 250 380' },

    // Doora -> Fact checker
    { from: 'doora', to: 'verify', path: 'M 250 380 L 250 220' },

    // Fact Checker -> Outputs
    { from: 'verify', to: 'out1', path: 'M 250 220 Q 185 160 120 100' },
    { from: 'verify', to: 'output', path: 'M 250 220 L 250 80' },
    { from: 'verify', to: 'out2', path: 'M 250 220 Q 315 160 380 100' },
  ];

  const pathVariants = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: { 
      pathLength: 1, 
      opacity: 0.2,
      transition: { duration: 1.5, ease: 'easeInOut' }
    }
  };

  const flowVariants = {
    animate: (custom) => ({
      pathOffset: [0, 1],
      opacity: [0, 1, 0],
      transition: {
        duration: 2.5,
        delay: custom * 0.4,
        repeat: Infinity,
        ease: 'linear'
      }
    })
  };

  return (
    <div className="relative w-full h-full min-h-[600px] bg-zinc-950 border border-zinc-800 rounded-3xl shadow-sm overflow-hidden flex flex-col justify-center p-6">
      {/* Background Grid */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none" 
        style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '16px 16px' }}
      />
      
      {/* Header element */}
      <div className="absolute top-6 left-6 right-6 flex items-start justify-between z-20">
        <div>
          <h3 className="font-semibold text-zinc-100 text-lg">{lang.header.title}</h3>
          <p className="text-xs text-zinc-400 font-medium tracking-wide uppercase mt-1">{lang.header.subtitle}</p>
        </div>
      </div>

      <svg
        ref={svgRef}
        viewBox="0 0 500 800"
        className="w-full h-full relative z-10 mt-12"
      >
        <defs>
          <linearGradient id="edgeGrad" x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="50%" stopColor="#8b5cf6" />
            <stop offset="100%" stopColor="#10b981" />
          </linearGradient>
          <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="8" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* Static Edges */}
        {edges.map((edge, i) => (
          <motion.path
            key={`static-${i}`}
            d={edge.path}
            fill="none"
            stroke="url(#edgeGrad)"
            strokeWidth="3"
            strokeDasharray="6 6"
            variants={pathVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          />
        ))}

        {/* Flow Animation along edges */}
        {edges.map((edge, i) => (
          <motion.path
            key={`flow-${i}`}
            d={edge.path}
            fill="none"
            stroke="url(#edgeGrad)"
            strokeWidth="4"
            strokeLinecap="round"
            filter="url(#glow)"
            custom={i}
            variants={flowVariants}
            initial={{ pathLength: 0.1, pathOffset: 0, opacity: 0 }}
            animate="animate"
          />
        ))}

        {/* Source citation badges popping up near output */}
        <motion.g
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2, duration: 0.5, repeat: Infinity, repeatType: 'reverse', repeatDelay: 3 }}
        >
          <rect x="270" y="40" width="70" height="24" rx="12" fill="white" stroke="#e4e4e7" strokeWidth="1.5" />
          <text x="305" y="56" fontSize="10" fontWeight="bold" fill="#71717a" textAnchor="middle">
            [1] [2] [3]
          </text>
        </motion.g>

        {/* Accuracy metric pinging near Doora node */}
        <motion.g
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.5, type: 'spring' }}
        >
          <rect x="140" y="360" width="64" height="24" rx="12" fill="#8b5cf6" opacity="0.1" />
          <text x="172" y="376" fontSize="10" fontWeight="bold" fill="#8b5cf6" textAnchor="middle">
            {lang.matchBadge}
          </text>
        </motion.g>

        {/* Nodes */}
        {nodes.map((node, i) => {
          const Icon = node.icon;
          return (
            <motion.g
              key={node.id}
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.2 + 0.5, type: 'spring', stiffness: 200 }}
            >
              {/* Outer Glow */}
              <circle
                cx={node.x}
                cy={node.y}
                r="28"
                fill={node.color}
                opacity="0.15"
                filter="url(#glow)"
              />
              {/* Node Circle */}
              <circle
                cx={node.x}
                cy={node.y}
                r="22"
                fill="black"
                stroke={node.color}
                strokeWidth="2.5"
              />
              {/* Icon (using SVG paths or foreignObject) */}
              <foreignObject x={node.x - 12} y={node.y - 12} width="24" height="24">
                <div xmlns="http://www.w3.org/1999/xhtml" className="w-full h-full flex items-center justify-center">
                  <Icon size={14} color={node.color} strokeWidth={2.5} />
                </div>
              </foreignObject>
              
              {/* Label Background */}
              <rect
                x={node.x - 60}
                y={node.y + 32}
                width="120"
                height="22"
                rx="4"
                fill="transparent"
                opacity="0.9"
              />
              {/* Label */}
              <text
                x={node.x}
                y={node.y + 47}
                textAnchor="middle"
                fontSize="11"
                fontWeight="600"
                fill="#ffffff"
              >
                {node.label}
              </text>
            </motion.g>
          );
        })}
      </svg>
    </div>
  );
};

export default KnowledgeGraph;